import { EnrollmentGroup } from './enrollment-group.model';
import { PeriodicReport } from './periodic-report.model';
import { Program } from './program.model';
import { Document } from './document.model';
import { ReportResponse } from './report-response.model';
import { SiteDocusignEnvelope } from './site-docusign-envelope.model';
import { SiteReviewCheck } from './site_review_check.model';
import { SiteReviewNote } from './site_review_note.model';
import { User } from './user.model';
import { Version } from './version.model';
import { YieldCurve } from './yield-curve.model';

import { Type } from '../decorators/type.decorator';
import { AcrStrataVersion } from "./acr-strata-version.model";
import { PaymentPeriod } from "./payment-period.model";
import { SiteBase } from '@shared/models/site-base.model';

export class Site extends SiteBase {
  @Type(User) public applicant: User;
  @Type(User) public assignedAdmin?: User;
  @Type(EnrollmentGroup) public availableEnrollmentGroups: EnrollmentGroup[];
  @Type(Document) public documents: Document[];
  @Type(PeriodicReport) public periodicReports: PeriodicReport[];
  @Type(PaymentPeriod) public paymentPeriods: PaymentPeriod[];
  @Type(ReportResponse) public reportResponses: ReportResponse[];
  @Type(Program) public programs: Program[];
  @Type(SiteDocusignEnvelope) public siteDocusignEnvelopes: SiteDocusignEnvelope[];
  @Type(SiteReviewCheck) public siteReviewChecks?: SiteReviewCheck[];
  @Type(SiteReviewNote) public siteReviewNotes?: SiteReviewNote[];
  @Type(Version) public versions: Version[];
  @Type(YieldCurve) public yieldCurves: YieldCurve[];
  @Type(AcrStrataVersion) public acrVersions: AcrStrataVersion[];

  getDate(property: any) {
    return property ? new Date(property + 'T00:00:00') : null;
  }

  getEnrollmentGroupFinancials(enrollmentGroupId: number, isCustom: boolean) {
    return this.financials.enrollment_group_financials.find(obj => obj.enrollment_group_id === enrollmentGroupId && obj.is_custom === isCustom);
  }

  getFirewoodDeductionRange(enrollmentGroupId: number = null) {
    let values = [];
    this.financials.enrollment_group_financials.forEach(enrollmentGroup => {
      let isGroupCustom = enrollmentGroup.is_custom || false;
      if (enrollmentGroupId === null || (enrollmentGroupId == enrollmentGroup.enrollment_group_id && this.acceptedCustomTerms == isGroupCustom)) {
        values.push(enrollmentGroup.firewood_deduction);
      }
    });
    if (values.length > 0) {
      return values.sort((a, b) => a - b);
    }
    return [0];
  }

  getClearingDeductionRange(enrollmentGroupId: number = null) {
    let values = [];
    this.financials.enrollment_group_financials.forEach(enrollmentGroup => {
      if (enrollmentGroupId === null || (enrollmentGroupId == enrollmentGroup.enrollment_group_id && this.acceptedCustomTerms == enrollmentGroup.is_custom)) {
        values.push(enrollmentGroup.clearing_deduction);
      }
    });
    if (values.length > 0) {
      return values.sort((a, b) => a - b);
    }
    return [0];
  }

  getAverageAnnualRevenue(enrollmentGroupId: number = null) {
    let values = [];
    this.financials.enrollment_group_financials.forEach(enrollmentGroup => {
      if ((enrollmentGroupId === null || (enrollmentGroupId == enrollmentGroup.enrollment_group_id && this.acceptedCustomTerms == enrollmentGroup.is_custom)) && enrollmentGroup.annual !== undefined) {
        values.push(enrollmentGroup.annual);
      }
    });
    if (values.length > 0) {
      return values.sort((a, b) => a - b);
    }
    return [0];
  }

  getTwentyYearProjection(enrollmentGroupId: number = null) {
    let values = [];
    this.financials.enrollment_group_financials.forEach(enrollmentGroup => {
      if ((enrollmentGroupId === null || (enrollmentGroupId == enrollmentGroup.enrollment_group_id && this.acceptedCustomTerms == enrollmentGroup.is_custom)) && enrollmentGroup.total !== undefined) {
        let enrollmentGroup_projection = enrollmentGroup.total.total ? enrollmentGroup.total.total : 0;
        values.push(enrollmentGroup_projection);
      }
    });
    if (values.length > 0) {
      return values.sort((a, b) => a - b);
    }
    return [0];
  }

  getYearMarkString(enrollmentGroupId: number = null) {
    const yearMarks = [];
    this.financials.enrollment_group_financials.forEach(enrollmentGroup => {
      if (enrollmentGroupId === null || (enrollmentGroupId == enrollmentGroup.enrollment_group_id && this.acceptedCustomTerms == enrollmentGroup.is_custom)) {
        yearMarks.push(enrollmentGroup.year_site_end);
      }
    });
    if (yearMarks.length > 0) {
      const minYear = Math.min(...yearMarks);
      const maxYear = Math.max(...yearMarks);
      let yearMarkString = minYear.toString();
      if (minYear !== maxYear) {
        yearMarkString += '/' + maxYear.toString();
      }
      return yearMarkString;
    }
    return '';
  }

  isFirstYear() {
    const first = this.financials?.enrollment_group_financials?.[0];
    return this.submittedDate == null || (first?.first_year ?? true);
  }

  getAnnualRevenueIconText() {
    if (this.isFirstYear()) {
      return 'Estimated annual revenue is the base payout for the next year of payments. This value can change based on your responses to periodic reporting or on market performance.'
    } else {
      return 'Total Revenue paid for the previous year of payments.'
    }
  }
}


export class SiteAvailableEnrollmentGroupRow extends EnrollmentGroup {
  public siteId: number;
  public acrProjectName: string;
  public regionName: string;
  public siteStatus: string;
  public siteName: string;
  public selected: boolean;
}

export class SiteDashboard extends Site {
  acrProjectId: string;
  applicantEmail: string;
  applicantStripeStatus: boolean;
  applicantLastLogin: Date;
  applicantName: string;
  applicantPhone: string;
  applicantStreet1: string;
  applicantStreet2: string;
  applicantCity: string;
  applicantState: string;
  applicantZip: string;
  assignedAdminName: string;
  delegateEmail: string;
  delegateName: string;
  delegateId: string;
  enrollmentGroupName: string;
  parcelCounty: string;
  parcelState: string;
  regionName: string;
  siteStatusCategory: string;
}
